import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from 'recharts';

// Supabase configuration
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_KEY || '';
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

const MOUNTAINS = ['Copper Mountain', 'Winter Park', 'A-Basin'];

// Minimum date for data availability (no data before this date)
const MIN_DATA_DATE = new Date(2026, 0, 1); // January 1, 2026

const getDisplayName = (name) => name === 'Wash Park' ? 'Denver' : name;

const fetchSnowConditions = async () => {
  // Check cache first (15 min cache)
  const cached = localStorage.getItem('snotelData');
  const cachedTime = localStorage.getItem('snotelDataTimestamp');
  const now = new Date();

  if (cached && cachedTime) {
    const lastFetch = new Date(cachedTime);
    const minutesSince = (now - lastFetch) / (1000 * 60);
    if (minutesSince < 15) {
      return JSON.parse(cached);
    }
  }

  // Fetch from Supabase Edge Function
  try {
    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/snow-conditions`,
      {
        headers: {
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        }
      }
    );
    const result = await response.json();

    if (result.status === 'success') {
      // Transform to keyed object
      const snowData = {};
      result.data.forEach(item => {
        snowData[item.resort] = {
          snow24h: item.snow24h,
          currentDepth: item.currentDepth,
          elevation: item.elevation,
          stationName: item.stationName
        };
      });

      // Cache it
      localStorage.setItem('snotelData', JSON.stringify(snowData));
      localStorage.setItem('snotelDataTimestamp', now.toISOString());

      return snowData;
    }
  } catch (e) {
    console.error('Error fetching snow conditions:', e);
  }

  // Fallback mock data
  return {
    'Copper Mountain': { snow24h: 0, currentDepth: 20, elevation: '10,500 ft' },
    'Winter Park': { snow24h: 0, currentDepth: 26, elevation: '11,300 ft' },
    'A-Basin': { snow24h: 0, currentDepth: 24, elevation: '11,410 ft' }
  };
};

const fetchHistoricalData = async (date, origin, destination) => {
  // JavaScript Date handles timezone automatically
  // Set to start of selected day in browser's local timezone (MT)
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);
  
  // End of selected day
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  
  // toISOString() converts to UTC automatically
  const startTime = startOfDay.toISOString();
  const endTime = endOfDay.toISOString();
  
  const { data, error } = await supabase
    .from('traffic_data')
    .select('*')
    .eq('origin', origin)
    .eq('destination', destination)
    .gte('timestamp', startTime)
    .lte('timestamp', endTime)
    .order('timestamp', { ascending: true });

  if (error || !data || data.length === 0) return null;

  return data.map(record => {
    // new Date() automatically converts UTC timestamp to browser's local timezone
    const localTime = new Date(record.timestamp);
    const h = localTime.getHours();
    const m = localTime.getMinutes();
    const duration = Math.round(record.duration_seconds / 60);
    
    // Status based on delay over base time (~75 mins)
    // Moderate = 25-30 min delay (100-105+ mins total)
    let status = 'light';
    if (duration > 150) status = 'severe';
    else if (duration > 120) status = 'heavy';
    else if (duration > 100) status = 'moderate';

    return {
      time: `${h > 12 ? h - 12 : h === 0 ? 12 : h}:${m < 10 ? '0' : ''}${m} ${h >= 12 ? 'PM' : 'AM'}`,
      duration_minutes: duration,
      duration_formatted: `${Math.floor(duration/60)}hr ${duration%60}min`,
      status: status
    };
  });
};

// Merge traffic data from multiple routes by time
const mergeRouteData = (copperData, winterParkData, abasinData) => {
  if (!copperData && !winterParkData && !abasinData) return null;

  // Collect all unique times from all datasets
  const timeMap = new Map();

  [copperData, winterParkData, abasinData].forEach((dataset, routeIndex) => {
    if (!dataset) return;
    dataset.forEach(record => {
      if (!timeMap.has(record.time)) {
        timeMap.set(record.time, {
          time: record.time,
          duration_minutes: record.duration_minutes, // Use first available for backward compatibility
          duration_formatted: record.duration_formatted,
          status: record.status
        });
      }
      const entry = timeMap.get(record.time);
      if (routeIndex === 0) entry.duration_copper = record.duration_minutes;
      else if (routeIndex === 1) entry.duration_winterpark = record.duration_minutes;
      else entry.duration_abasin = record.duration_minutes;
    });
  });

  // Convert map to array and sort by time
  return Array.from(timeMap.values()).sort((a, b) => {
    // Parse time strings like "5:00 AM" for sorting
    const parseTime = (timeStr) => {
      const [time, period] = timeStr.split(' ');
      const [hours, minutes] = time.split(':').map(Number);
      let hour24 = hours;
      if (period === 'PM' && hours !== 12) hour24 += 12;
      if (period === 'AM' && hours === 12) hour24 = 0;
      return hour24 * 60 + minutes;
    };
    return parseTime(a.time) - parseTime(b.time);
  });
};

function App() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [origin, setOrigin] = useState('Wash Park');
  const [destination, setDestination] = useState('Copper Mountain');
  const [trafficData, setTrafficData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [snowData, setSnowData] = useState({});
  const [noDataAvailable, setNoDataAvailable] = useState(false);
  const [showAllRoutes, setShowAllRoutes] = useState(false);

  useEffect(() => {
    const loadSnow = async () => {
      const data = await fetchSnowConditions();
      setSnowData(data);
    };
    loadSnow();
  }, []);

  // Reset showAllRoutes when neither origin nor destination is Wash Park
  useEffect(() => {
    if (origin !== 'Wash Park' && destination !== 'Wash Park') {
      setShowAllRoutes(false);
    }
  }, [origin, destination]);

  // Fetch single route data for traffic list
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const data = await fetchHistoricalData(selectedDate, origin, destination);
      setTrafficData(data || []);
      setChartData(data || []); // Also set chart data for single route
      setNoDataAvailable(!data);
      setLoading(false);
    };
    fetchData();
  }, [selectedDate, destination, origin]);

  // Fetch chart data for multi-route view
  useEffect(() => {
    const fetchChartData = async () => {
      if (showAllRoutes) {
        if (origin === 'Wash Park') {
          // Denver → All Mountains
          const [copperData, winterParkData, abasinData] = await Promise.all([
            fetchHistoricalData(selectedDate, 'Wash Park', 'Copper Mountain'),
            fetchHistoricalData(selectedDate, 'Wash Park', 'Winter Park'),
            fetchHistoricalData(selectedDate, 'Wash Park', 'A-Basin')
          ]);
          const mergedData = mergeRouteData(copperData, winterParkData, abasinData);
          setChartData(mergedData || []);
        } else if (destination === 'Wash Park') {
          // All Mountains → Denver
          const [copperData, winterParkData, abasinData] = await Promise.all([
            fetchHistoricalData(selectedDate, 'Copper Mountain', 'Wash Park'),
            fetchHistoricalData(selectedDate, 'Winter Park', 'Wash Park'),
            fetchHistoricalData(selectedDate, 'A-Basin', 'Wash Park')
          ]);
          const mergedData = mergeRouteData(copperData, winterParkData, abasinData);
          setChartData(mergedData || []);
        }
      }
    };
    if (showAllRoutes && (origin === 'Wash Park' || destination === 'Wash Park')) {
      fetchChartData();
    }
  }, [selectedDate, origin, destination, showAllRoutes]);

  const handleSwap = () => {
    setOrigin(destination);
    setDestination(origin);
  };

  return (
    <div className="container" style={styles.container}>
      <style>{`
        @media (max-width: 900px) {
          .main-grid {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
          header h1 { font-size: 2rem !important; }
          .left-column, .right-column { width: 100% !important; }
        }

        @media (max-width: 768px) {
          .main-grid { gap: 16px !important; }
          .route-card-controls {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          .route-card-controls > div:nth-child(2) {
            order: 2;
            display: flex;
            justify-content: center;
            margin: 0 !important;
          }
          .traffic-row {
            flex-wrap: wrap !important;
            justify-content: space-between !important;
          }
          .traffic-row > div:nth-child(3) {
            flex: 1 0 100% !important;
            margin: 8px 0 0 0 !important;
          }
          .chartContainer {
            padding: 20px !important;
            margin-top: 20px !important;
          }
        }

        @media (max-width: 480px) {
          .container { padding: 12px 12px 32px !important; }
          .card { padding: 20px !important; }
          .main-grid { gap: 12px !important; }
          header h1 { font-size: 1.6rem !important; }
          .calendar-day { height: 36px !important; }
          .chartContainer {
            padding: 16px !important;
          }
        }
      `}</style>
      <header style={styles.header}>
        <div style={styles.logo}>
          <MountainIcon />
          <h1 style={styles.title}>I-70 Mountain Traffic</h1>
        </div>
        <p style={styles.subtitle}>Plan your mountain adventure with historical traffic data</p>
      </header>

      <main className="main-grid" style={styles.main}>
        <div className="left-column" style={styles.leftColumn}>
          <div className="card" style={styles.card}>
            <h2 style={styles.cardTitle}>Select Date</h2>
            <Calendar 
              currentMonth={currentMonth}
              setCurrentMonth={setCurrentMonth}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
            />
            <div style={styles.legend}>
              <span style={styles.legendItem}><span style={{...styles.legendDot, background: '#EEF2FF'}}></span> Weekend</span>
              <span style={styles.legendItem}><span style={{...styles.legendDot, background: '#CFFAFE'}}></span> Holiday</span>
              <span style={styles.legendItem}><span style={{...styles.legendDot, background: '#7C3AED'}}></span> Selected</span>
            </div>
            
            <div style={styles.snowConditions}>
              <h3 style={styles.snowTitle}>Snow Conditions</h3>
              <div style={styles.snowList}>
                {Object.keys(snowData).map(mountain => {
                  const data = snowData[mountain];
                  const snow24h = data.snow24h || 0;
                  return (
                    <div key={mountain} style={styles.weatherItem}>
                      <div style={styles.snowMountain}>
                        <SnowflakeIcon />
                        <span>{mountain}</span>
                      </div>
                      <div style={styles.weatherInfo}>
                        <span style={{...styles.snow24h, color: snow24h > 0 ? '#0EA5E9' : '#94A3B8'}}>
                          {snow24h > 0 ? `+${snow24h}"` : '0"'}
                        </span>
                        <span style={styles.snowLabel}>24hr</span>
                        <span style={styles.snowDepth}>{data.currentDepth}" base</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={styles.snotelCredit}>Data from USDA SNOTEL</div>
            </div>
          </div>
        </div>

        <div className="right-column" style={styles.rightColumn}>
          <div className="card" style={styles.routeCard}>
            <h2 style={styles.cardTitle}>Plan Your Route</h2>
            <div className="route-card-controls" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {origin === 'Wash Park' ? <CityIcon /> : <MountainSmallIcon />}
                    Depart From
                  </div>
                </label>
                <select style={styles.select} value={origin} onChange={(e) => setOrigin(e.target.value)}>
                  <option value="Wash Park">Denver</option>
                  {MOUNTAINS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              
              <div style={{ marginTop: 20 }}>
                <button onClick={handleSwap} style={styles.swapButton} title="Swap origin and destination">
                  <FlipIcon />
                </button>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {destination === 'Wash Park' ? <CityIcon /> : <MountainSmallIcon />}
                    Destination
                  </div>
                </label>
                <select style={styles.select} value={destination} onChange={(e) => setDestination(e.target.value)}>
                  <option value="Wash Park">Denver</option>
                  {MOUNTAINS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="card" style={{...styles.card, flex: 1, display: 'flex', flexDirection: 'column'}}>
            <h2 style={{...styles.cardTitle, marginBottom: 16}}>Historical Traffic</h2>

            <p style={styles.dateHeader}>{selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</p>

            {loading ? <div style={styles.loading}>Loading...</div> :
             noDataAvailable ? <div style={styles.noData}><div style={styles.noDataText}>No historical data available</div></div> :
             <div style={styles.trafficList}>
               {trafficData.map((row, i) => (
                 <div key={i} className="traffic-row" style={styles.trafficRow}>
                   <div style={styles.timeCol}>
                     <ClockIcon />
                     <div>
                       <div style={styles.timeValue}>{row.time}</div>
                       <div style={styles.timeLabel}>Departure</div>
                     </div>
                   </div>
                   <div style={styles.durationCol}>
                     <TrendIcon />
                     <div>
                       <div style={styles.durationValue}>{row.duration_formatted}</div>
                       <div style={styles.durationLabel}>Drive Time</div>
                     </div>
                   </div>
                   <div style={{...styles.statusBadge, background: row.status === 'heavy' ? '#F97316' : row.status === 'moderate' ? '#F59E0B' : '#10B981'}}>
                     <span style={styles.statusDot}></span>
                     {row.status.charAt(0).toUpperCase() + row.status.slice(1)} Traffic
                   </div>
                 </div>
               ))}
             </div>
            }
            <div style={styles.statusLegend}>
              {['light', 'moderate', 'heavy', 'severe'].map(s => (
                <div key={s} style={styles.statusLegendItem}>
                  <span style={{...styles.statusLegendDot, background: s === 'light' ? '#10B981' : s === 'moderate' ? '#F59E0B' : s === 'heavy' ? '#F97316' : '#EF4444'}}></span>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Traffic Chart - Full Width Below Grid */}
      <TrafficChart
        data={chartData}
        selectedDate={selectedDate}
        origin={origin}
        destination={destination}
        showAllRoutes={showAllRoutes}
        setShowAllRoutes={setShowAllRoutes}
      />

    </div>
  );
}

// Sub-components
function Calendar({ currentMonth, setCurrentMonth, selectedDate, setSelectedDate }) {
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const getDaysInMonth = (date) => {
    const days = [];
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
    for (let i = 1; i <= lastDay.getDate(); i++) days.push(new Date(date.getFullYear(), date.getMonth(), i));
    return days;
  };

  const isHoliday = (day) => {
    if (!day) return false;
    const m = day.getMonth();
    const d = day.getDate();
    const dow = day.getDay(); // 0=Sun, 1=Mon, etc.

    // Fixed holidays
    if (m === 0 && d === 1) return true; // New Year's Day
    if (m === 6 && d === 4) return true; // July 4th
    if (m === 11 && d >= 24 && d <= 26) return true; // Christmas Eve/Day/after
    if (m === 11 && d === 31) return true; // New Year's Eve

    // Floating holidays (Monday holidays)
    // MLK Day: 3rd Monday of January
    if (m === 0 && dow === 1 && d >= 15 && d <= 21) return true;
    // Presidents Day: 3rd Monday of February
    if (m === 1 && dow === 1 && d >= 15 && d <= 21) return true;
    // Memorial Day: Last Monday of May
    if (m === 4 && dow === 1 && d >= 25) return true;
    // Labor Day: 1st Monday of September
    if (m === 8 && dow === 1 && d <= 7) return true;

    // Thanksgiving: 4th Thursday of November (and Fri after)
    if (m === 10 && dow === 4 && d >= 22 && d <= 28) return true;
    if (m === 10 && dow === 5 && d >= 23 && d <= 29) return true;

    return false;
  };

  const isSelected = (d) => d && d.toDateString() === selectedDate.toDateString();
  const isWeekend = (d) => d && (d.getDay() === 0 || d.getDay() === 6);
  const isBeforeMinDate = (d) => d && d < MIN_DATA_DATE;

  // Check if we can navigate to previous month (don't go before January 2026)
  const canGoPrevMonth = currentMonth.getFullYear() > MIN_DATA_DATE.getFullYear() ||
    (currentMonth.getFullYear() === MIN_DATA_DATE.getFullYear() && currentMonth.getMonth() > MIN_DATA_DATE.getMonth());

  return (
    <div style={calStyles.calendar}>
      <div style={calStyles.header}>
        <button
          onClick={() => canGoPrevMonth && setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
          style={{...calStyles.navButton, opacity: canGoPrevMonth ? 1 : 0.3, cursor: canGoPrevMonth ? 'pointer' : 'default'}}
        >&lt;</button>
        <div style={calStyles.monthName}>{currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</div>
        <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} style={calStyles.navButton}>&gt;</button>
      </div>
      <div style={calStyles.weekdays}>
        {daysOfWeek.map(d => <div key={d} style={calStyles.weekday}>{d}</div>)}
      </div>
      <div style={calStyles.daysGrid}>
        {getDaysInMonth(currentMonth).map((day, i) => {
          const disabled = isBeforeMinDate(day);
          return (
            <div key={i} className="calendar-day" onClick={() => day && !disabled && setSelectedDate(day)} style={{
              ...calStyles.day,
              ...(day && !disabled ? calStyles.dayClickable : {}),
              ...(disabled ? calStyles.dayDisabled : {}),
              ...(isWeekend(day) && !isSelected(day) && !isHoliday(day) && !disabled ? calStyles.dayWeekend : {}),
              ...(isHoliday(day) && !isSelected(day) && !disabled ? calStyles.dayHoliday : {}),
              ...(isSelected(day) && !disabled ? calStyles.daySelected : {}),
            }}>
              {day ? day.getDate() : ''}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Traffic Chart Component
function TrafficChart({ data, selectedDate, origin, destination, showAllRoutes, setShowAllRoutes }) {
  // Format minutes to "Xhr Ymin" for tooltip
  const formatDuration = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs === 0) return `${mins}min`;
    if (mins === 0) return `${hrs}hr`;
    return `${hrs}hr ${mins}min`;
  };

  if (!data || data.length === 0) {
    return (
      <div className="card chartContainer" style={styles.chartContainer}>
        <div style={styles.noChartData}>
          <div style={styles.noDataText}>No traffic data available for this date</div>
        </div>
      </div>
    );
  }

  return (
    <div className="card chartContainer" style={styles.chartContainer}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <h2 style={{...styles.cardTitle, marginBottom: 4}}>Drive Time Throughout Day</h2>
          <p style={{...styles.chartSubtitle, marginTop: 0}}>
            {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })} -
            {' '}{showAllRoutes ? (origin === 'Wash Park' ? 'Denver → All Routes' : 'All Routes → Denver') : `${getDisplayName(origin)} → ${getDisplayName(destination)}`}
          </p>
        </div>
        {(origin === 'Wash Park' || destination === 'Wash Park') && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem', color: '#64748B', whiteSpace: 'nowrap', marginTop: 4 }}>
            <input
              type="checkbox"
              checked={showAllRoutes}
              onChange={(e) => setShowAllRoutes(e.target.checked)}
              style={{ cursor: 'pointer', width: 16, height: 16 }}
            />
            Compare All Routes
          </label>
        )}
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />

          {/* Background color zones for traffic levels */}
          <ReferenceArea y1={0} y2={100} fill="#10B981" fillOpacity={0.2} />
          <ReferenceArea y1={100} y2={120} fill="#F59E0B" fillOpacity={0.25} />
          <ReferenceArea y1={120} y2={150} fill="#F97316" fillOpacity={0.25} />
          <ReferenceArea y1={150} y2={1000} fill="#DC2626" fillOpacity={0.2} />
          <XAxis
            dataKey="time"
            stroke="#64748B"
            style={{ fontSize: '0.8rem' }}
            interval={1}
            angle={-45}
            textAnchor="end"
            height={70}
          />
          <YAxis
            stroke="#64748B"
            label={{ value: 'Minutes', angle: -90, position: 'insideLeft', style: { fontSize: '0.9rem', fill: '#64748B' } }}
            style={{ fontSize: '0.85rem' }}
            domain={[0, 160]}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #E2E8F0',
              borderRadius: 8,
              padding: 12
            }}
            labelStyle={{ fontWeight: 600, marginBottom: 4 }}
            formatter={(value, name) => {
              if (!value) return null;
              const routeName = name === 'duration_copper' ? 'Copper Mountain' :
                                name === 'duration_winterpark' ? 'Winter Park' :
                                name === 'duration_abasin' ? 'A-Basin' : 'Drive Time';
              return [formatDuration(value), routeName];
            }}
          />
          {showAllRoutes ? (
            // Render all three routes - selected destination/origin first
            <>
              {/* Determine which mountain is selected (destination for outbound, origin for return) */}
              {(destination === 'Copper Mountain' || origin === 'Copper Mountain') && (
                <Line
                  type="monotone"
                  dataKey="duration_copper"
                  name="duration_copper"
                  stroke="#7C3AED"
                  strokeWidth={3}
                  strokeOpacity={1}
                  dot={{ fill: '#7C3AED', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              )}
              {(destination === 'Winter Park' || origin === 'Winter Park') && (
                <Line
                  type="monotone"
                  dataKey="duration_winterpark"
                  name="duration_winterpark"
                  stroke="#7C3AED"
                  strokeWidth={3}
                  strokeOpacity={1}
                  dot={{ fill: '#7C3AED', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              )}
              {(destination === 'A-Basin' || origin === 'A-Basin') && (
                <Line
                  type="monotone"
                  dataKey="duration_abasin"
                  name="duration_abasin"
                  stroke="#7C3AED"
                  strokeWidth={3}
                  strokeOpacity={1}
                  dot={{ fill: '#7C3AED', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              )}
              {destination !== 'Copper Mountain' && origin !== 'Copper Mountain' && (
                <Line
                  type="monotone"
                  dataKey="duration_copper"
                  name="duration_copper"
                  stroke="#F97316"
                  strokeWidth={2}
                  strokeOpacity={0.4}
                  dot={false}
                  activeDot={{ r: 6 }}
                />
              )}
              {destination !== 'Winter Park' && origin !== 'Winter Park' && (
                <Line
                  type="monotone"
                  dataKey="duration_winterpark"
                  name="duration_winterpark"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  strokeOpacity={0.4}
                  dot={false}
                  activeDot={{ r: 6 }}
                />
              )}
              {destination !== 'A-Basin' && origin !== 'A-Basin' && (
                <Line
                  type="monotone"
                  dataKey="duration_abasin"
                  name="duration_abasin"
                  stroke="#10B981"
                  strokeWidth={2}
                  strokeOpacity={0.4}
                  dot={false}
                  activeDot={{ r: 6 }}
                />
              )}
            </>
          ) : (
            // Single route (current behavior)
            <Line
              type="monotone"
              dataKey="duration_minutes"
              stroke="#7C3AED"
              strokeWidth={3}
              dot={{ fill: '#7C3AED', r: 4 }}
              activeDot={{ r: 6 }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>

      {/* Legend for traffic status colors */}
      <div style={styles.chartLegend}>
        <div style={styles.legendTitle}>Traffic Levels:</div>
        <div style={styles.legendItems}>
          <div style={styles.legendItem}>
            <div style={{...styles.legendColorBox, background: '#10B981'}}></div>
            <span>Light (&lt;100 min)</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{...styles.legendColorBox, background: '#F59E0B'}}></div>
            <span>Moderate (100-120 min)</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{...styles.legendColorBox, background: '#F97316'}}></div>
            <span>Heavy (120-150 min)</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{...styles.legendColorBox, background: '#DC2626'}}></div>
            <span>Severe (&gt;150 min)</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Icons
const MountainIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
  </svg>
);

const MountainSmallIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: 12}}>
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);

const TrendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: 12}}>
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
  </svg>
);

const FlipIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="m17 11 4-4-4-4"/><path d="M21 7H3"/><path d="m7 21-4-4 4-4"/><path d="M3 17h18"/>
  </svg>
);

const CityIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><line x1="9" y1="22" x2="9" y2="22"/><line x1="15" y1="22" x2="15" y2="22"/><line x1="12" y1="22" x2="12" y2="22"/><path d="M12 2v20"/><path d="M9 6h0"/><path d="M15 6h0"/><path d="M9 10h0"/><path d="M15 10h0"/><path d="M9 14h0"/><path d="M15 14h0"/><path d="M9 18h0"/><path d="M15 18h0"/>
  </svg>
);

const SnowflakeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0EA5E9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="2" x2="12" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/><line x1="19.07" y1="4.93" x2="4.93" y2="19.07"/><line x1="12" y1="2" x2="9" y2="5"/><line x1="12" y1="2" x2="15" y2="5"/><line x1="12" y1="22" x2="9" y2="19"/><line x1="12" y1="22" x2="15" y2="19"/>
  </svg>
);

// Styles
const styles = {
  container: { maxWidth: 1200, margin: '0 auto', padding: '20px 20px 40px', fontFamily: 'Inter, system-ui, sans-serif', backgroundColor: '#F8FAFC', minHeight: '100vh' },
  header: { textAlign: 'center', marginBottom: 40, marginTop: 20 },
  logo: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 12 },
  title: { fontSize: '2.5rem', fontWeight: 800, color: '#1E293B', margin: 0, letterSpacing: '-0.025em' },
  subtitle: { fontSize: '1.1rem', color: '#64748B', margin: 0 },
  main: { display: 'grid', gridTemplateColumns: '420px 1fr', gap: 32, alignItems: 'stretch' },
  leftColumn: { display: 'flex' },
  rightColumn: { display: 'flex', flexDirection: 'column', gap: 32 },
  card: { background: 'white', borderRadius: 20, padding: 32, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', width: '100%' },
  routeCard: { background: 'white', borderRadius: 20, padding: '24px 32px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', width: '100%' },
  cardTitle: { fontSize: '1.2rem', fontWeight: 700, color: '#1E293B', marginBottom: 24, marginTop: 0 },
  label: { display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#64748B', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.025em' },
  select: { width: '100%', padding: '12px 16px', borderRadius: 12, border: '2px solid #E2E8F0', fontSize: '1rem', color: '#1E293B', cursor: 'pointer', appearance: 'none', background: 'white', fontWeight: 500 },
  formGroup: { marginBottom: 0, flex: 1 },
  swapButtonContainer: { display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 24 },
  swapButton: { background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#7C3AED', transition: 'all 0.2s', padding: 0 },
  dateHeader: { fontSize: '1rem', color: '#64748B', marginBottom: 20, fontWeight: 500 },
  trafficList: { flex: 1, overflowY: 'auto', maxHeight: 550, minHeight: 0, paddingRight: 8 },
  trafficRow: { display: 'flex', alignItems: 'center', padding: '16px 20px', borderRadius: 16, marginBottom: 12, border: '1px solid #F1F5F9', background: 'white' },
  timeCol: { display: 'flex', alignItems: 'center', flex: '0 0 120px' },
  timeValue: { fontWeight: 600, fontSize: '1rem' },
  timeLabel: { fontSize: '0.75rem', color: '#64748B' },
  durationCol: { display: 'flex', alignItems: 'center', flex: '0 0 120px' },
  durationValue: { fontWeight: 600, fontSize: '1rem', whiteSpace: 'nowrap' },
  durationLabel: { fontSize: '0.75rem', color: '#64748B' },
  statusBadge: { marginLeft: 'auto', padding: '8px 16px', borderRadius: 20, color: 'white', fontWeight: 600, fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 6 },
  statusDot: { width: 8, height: 8, borderRadius: '50%', background: 'rgba(255,255,255,0.5)' },
  statusLegend: { display: 'flex', gap: 20, marginTop: 16, paddingTop: 16, borderTop: '1px solid #E2E8F0', flexWrap: 'wrap' },
  statusLegendItem: { display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: '#64748B' },
  statusLegendDot: { width: 10, height: 10, borderRadius: '50%' },
  loading: { padding: 40, textAlign: 'center', color: '#64748B' },
  noData: { padding: 60, textAlign: 'center', color: '#64748B' },
  noDataText: { fontSize: '1.1rem', fontWeight: 600, color: '#475569' },
  legend: { display: 'flex', gap: 16, marginTop: 20, paddingTop: 20, borderTop: '1px solid #F1F5F9' },
  legendItem: { display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', color: '#64748B' },
  legendDot: { width: 12, height: 12, borderRadius: 4 },
  snowConditions: { marginTop: 32, paddingTop: 32, borderTop: '1px solid #F1F5F9' },
  snowTitle: { fontSize: '1.2rem', fontWeight: 700, color: '#1E293B', marginBottom: 24, marginTop: 0 },
  snowList: { display: 'flex', flexDirection: 'column', gap: 16 },
  weatherItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 16, borderBottom: '1px solid #F1F5F9' },
  snowMountain: { display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.95rem', fontWeight: 500, color: '#1E293B', minWidth: 140 },
  weatherInfo: { display: 'flex', alignItems: 'center', gap: 8 },
  snow24h: { fontSize: '1.1rem', fontWeight: 700 },
  snowLabel: { fontSize: '0.75rem', color: '#94A3B8', marginRight: 8 },
  snowDepth: { fontSize: '0.9rem', color: '#64748B' },
  snotelCredit: { fontSize: '0.7rem', color: '#94A3B8', marginTop: 16, textAlign: 'right' },
  chartContainer: { background: 'white', borderRadius: 20, padding: 32, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', width: '100%', marginTop: 32 },
  chartSubtitle: { fontSize: '0.95rem', color: '#64748B', marginTop: -16, marginBottom: 24 },
  noChartData: { padding: 60, textAlign: 'center', color: '#64748B' },
  chartLegend: { marginTop: 24, paddingTop: 20, borderTop: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', gap: 12 },
  legendTitle: { fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: 4 },
  legendItems: { display: 'flex', gap: 20, flexWrap: 'wrap' },
  legendColorBox: { width: 16, height: 16, borderRadius: 4 }

};

const calStyles = {
  calendar: { padding: '4px 0' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  monthName: { fontWeight: 700, color: '#1E293B' },
  navButton: { background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', fontSize: '1.2rem' },
  weekdays: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 8 },
  weekday: { textAlign: 'center', fontSize: '0.75rem', fontWeight: 600, color: '#94A3B8' },
  daysGrid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 },
  day: { height: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: 8, fontSize: '0.9rem', fontWeight: 500 },
  dayClickable: { cursor: 'pointer' },
  dayDisabled: { color: '#CBD5E1', cursor: 'default' },
  dayWeekend: { background: '#EEF2FF', color: '#6366F1' },
  dayHoliday: { background: '#CFFAFE', color: '#0E7490' },
  daySelected: { background: '#7C3AED', color: 'white' },
};

export default App;
